/**
 * Tiny `fetch` helper for routes that need the active Clerk Bearer
 * token. Mirrors the pattern in src/pages/Dashboard.tsx but lives in
 * a shared module so the new agentic widgets don't each redeclare it.
 *
 * Throws on non-2xx so TanStack Query's `isError` flag flips. The
 * thrown error is an `AuthFetchError` that carries the HTTP status and
 * the parsed JSON body (when available) so callers can branch on 403
 * envelopes like `{ error: 'forbidden', required_permission, ... }`.
 */

export class AuthFetchError extends Error {
  status: number
  body: unknown

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `Request failed (${status})`)
    this.name = 'AuthFetchError'
    this.status = status
    this.body = body
  }
}

export async function authFetch<T>(
  url: string,
  getToken: () => Promise<string | null>,
  init?: RequestInit,
): Promise<T> {
  const token = await getToken()
  if (!token) throw new Error('Missing Clerk token')
  const res = await fetch(url, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    let parsed: unknown
    try {
      parsed = text ? JSON.parse(text) : null
    } catch {
      parsed = text || null
    }
    throw new AuthFetchError(res.status, parsed, text || `Request failed (${res.status})`)
  }
  return (await res.json()) as T
}
